import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getProductDetail, getShipping } from '../api/aliApi';
import { ClipboardIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import PaymentModal from '../components/payment/PaymentModal';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';

const countryMap = {
  CN: 'China',
  US: 'United States',
  NG: 'Nigeria',
};

const formatPrice = (price) => {
  if (price === null || price === undefined || Number.isNaN(Number(price))) return 'N/A';
  const numeric = Number(price);
  return numeric.toLocaleString('en-NG', { style: 'currency', currency: 'NGN' });
};

const getNumericShippingFee = (shippingFeeFormat = '') => {
  const fee = Number.parseFloat(String(shippingFeeFormat).replace(/[^\d.]/g, ''));
  return Number.isNaN(fee) ? 0 : fee;
};

const AliProductDetail = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShippingKey, setSelectedShippingKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      const data = await getProductDetail(productId);
      const productData = data?.full?.aliexpress_ds_product_get_response?.result;
      setProduct(productData);

      const sku = productData?.ae_item_sku_info_dtos?.ae_item_sku_info_d_t_o?.[0]?.sku_id;
      if (sku) {
        const ship = await getShipping(productId, sku, 'NG');
        const options = ship?.aliexpress_ds_freight_query_response?.result?.delivery_options?.delivery_option_d_t_o || [];
        setShippingOptions(options);
      }

      setLoading(false);
    };
    load();
  }, [productId]);

  useEffect(() => {
    if (!product) return;
    const imgs = document.querySelectorAll('.description-content img');
    imgs.forEach((img) => {
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';
      img.style.margin = '10px auto';
    });
  }, [product]);

  const handleCopyURL = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Product URL copied!');
  };

  const sortedShippingOptions = useMemo(() => {
    return [...shippingOptions]
      .map((option, idx) => ({
        ...option,
        key: `${option.company}-${option.shipping_fee_format}-${idx}`,
        numericFee: getNumericShippingFee(option.shipping_fee_format),
      }))
      .sort((a, b) => a.numericFee - b.numericFee);
  }, [shippingOptions]);

  useEffect(() => {
    if (!sortedShippingOptions.length) {
      setSelectedShippingKey('');
      return;
    }

    if (!selectedShippingKey) {
      setSelectedShippingKey(sortedShippingOptions[0].key);
      return;
    }

    const stillExists = sortedShippingOptions.some((option) => option.key === selectedShippingKey);
    if (!stillExists) {
      setSelectedShippingKey(sortedShippingOptions[0].key);
    }
  }, [selectedShippingKey, sortedShippingOptions]);

  if (loading || !product) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900" />
      </div>
    );
  }

  const images = product?.ae_multimedia_info_dto?.image_urls?.split(';').filter(Boolean) || [];
  const detailHTML = product?.ae_item_base_info_dto?.detail || '';
  const rawPrice = product?.ae_item_sku_info_dtos?.ae_item_sku_info_d_t_o?.[0]?.offer_sale_price;
  const basePrice = Number.parseFloat(rawPrice);
  const safeBasePrice = Number.isNaN(basePrice) ? 0 : basePrice;
  const currencycode = product?.ae_item_sku_info_dtos?.ae_item_sku_info_d_t_o?.[0]?.currency_code;
  const skuavailable = product?.ae_item_sku_info_dtos?.ae_item_sku_info_d_t_o?.[0]?.sku_available_stock;
  const title = product?.ae_item_base_info_dto?.subject || 'Untitled Product';
  const store = product?.ae_store_info?.store_name || 'Unknown Store';
  const formattedPrice = formatPrice(safeBasePrice);
  const productImage = images[0] || 'https://placehold.co/600x600';
  const productDescription =
    product?.ae_item_base_info_dto?.subject ||
    detailHTML.replace(/<[^>]+>/g, '').slice(0, 160);

  const selectedShippingOption = sortedShippingOptions.find((option) => option.key === selectedShippingKey) || null;
  const shippingFee = selectedShippingOption?.numericFee || 0;
  const totalCost = safeBasePrice + shippingFee;

  return (
    <>
      <Helmet>
        <title>{`${title} | KambiHome`}</title>
        <meta name="description" content={productDescription} />
        <meta name="keywords" content={`${title}, KambiHome, Nigeria, home and lifestyle products`} />
        <link rel="canonical" href={window.location.href} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={productDescription} />
        <meta property="og:image" content={productImage} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="product" />
      </Helmet>

      <Header />
      <div className="max-w-7xl mt-16 mx-auto px-4 py-8">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="flex flex-col md:flex-row">
            <div className="w-full md:w-1/2 p-4">
              <div className="rounded-lg overflow-hidden mb-3">
                <img
                  src={images[carouselIndex] || productImage}
                  alt={`Main ${carouselIndex}`}
                  className="w-full h-80 object-contain bg-gray-50"
                />
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCarouselIndex(idx)}
                    className={`cursor-pointer w-8 h-8 text-sm rounded-full border ${
                      idx === carouselIndex ? 'bg-green-600 text-white' : 'bg-white text-gray-700'
                    } hover:bg-green-500 hover:text-white transition`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
            </div>

            <div className="w-full md:w-1/2 p-6">
              <h2 className="text-2xl font-semibold text-gray-800">{title}</h2>
              <p className="text-green-600 text-xl mt-2 font-bold">
                {formattedPrice}{' '}
                <span className="text-sm bg-gray-200 text-gray-800 px-2 py-1 rounded">{currencycode}</span>
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Store: <strong>{store}</strong>
              </p>
              <p className="text-xs mt-1 bg-black text-white inline-block px-2 py-1 rounded">
                SKU Stock: {skuavailable}
              </p>

              {sortedShippingOptions.length > 0 && (
                <div className="mt-6 bg-gray-50 p-4 border rounded-md">
                  <h4 className="text-md font-semibold mb-1">Shipping Options to Nigeria</h4>
                  <p className="text-xs text-gray-500 mb-3">Ranked from cheapest to highest cost. Select one to calculate your total.</p>
                  <div className="grid grid-cols-1 gap-3 max-h-80 overflow-y-auto pr-1">
                    {sortedShippingOptions.map((option) => {
                      const isSelected = option.key === selectedShippingKey;

                      return (
                        <button
                          type="button"
                          key={option.key}
                          onClick={() => setSelectedShippingKey(option.key)}
                          className={`cursor-pointer text-left border rounded-lg p-3 transition ${
                            isSelected
                              ? 'border-green-600 ring-2 ring-green-100 bg-green-50'
                              : 'border-gray-200 bg-white hover:border-green-400'
                          }`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                            <p className="font-semibold text-gray-800">{option.company}</p>
                            <p className="text-green-700 font-semibold">{option.shipping_fee_format}</p>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">ETA: {option.delivery_date_desc || 'N/A'}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Delivery: {option.min_delivery_days}–{option.max_delivery_days} days · From:{' '}
                            {countryMap[option.ship_from_country] || option.ship_from_country}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-3 space-y-1">
                <p className="text-sm text-gray-700">
                  Product: <span className="font-semibold">{formatPrice(safeBasePrice)}</span>
                </p>
                <p className="text-sm text-gray-700">
                  Shipping: <span className="font-semibold">{selectedShippingOption?.shipping_fee_format || formatPrice(0)}</span>
                </p>
                <p className="text-base text-green-700 font-bold">
                  Total to Pay: {formatPrice(totalCost)}
                </p>
              </div>

              <div className="flex gap-3 mt-6 flex-wrap">
                <button
                  onClick={() => setIsPaymentModalOpen(true)}
                  className="cursor-pointer flex items-center gap-2 bg-green-600 hover:bg-green-700 hover:scale-105 text-white px-4 py-2 rounded transition-all duration-300"
                >
                  <ShoppingCartIcon className="w-5 h-5" />
                  Buy Now
                </button>
                <button
                  onClick={handleCopyURL}
                  className="cursor-pointer flex items-center gap-2 border border-gray-700 text-gray-800 px-4 py-2 rounded hover:bg-gray-100 hover:scale-105 transition-all duration-300"
                >
                  <ClipboardIcon className="w-5 h-5" />
                  Copy URL
                </button>
              </div>
            </div>
          </div>
        </div>

        {detailHTML && (
          <div className="mt-8 bg-white p-6 rounded shadow">
            <h3 className="text-lg font-semibold mb-4">Product Description</h3>
            <div
              className="description-content prose max-w-full overflow-x-auto"
              dangerouslySetInnerHTML={{ __html: detailHTML }}
            />
          </div>
        )}
      </div>
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        product={{
          title,
          formattedPrice,
          currencyCode: currencycode,
          productUrl: window.location.href,
          productImage,
          shippingCompany: selectedShippingOption?.company || 'Not selected',
          shippingFee: selectedShippingOption?.shipping_fee_format || formatPrice(0),
          totalCost: formatPrice(totalCost),
          shippingEta: selectedShippingOption?.delivery_date_desc || 'N/A',
        }}
      />
      <Footer />
    </>
  );
};

export default AliProductDetail;
